/**
 * Subunit Types
 * Independently leasable spaces within parent hangar units
 * (mezzanine offices, ground-level storage, crew rooms, shops)
 */

import type { Database } from '@planmax/shared-types'

// --------------------------------------------------------------------------
// Category (maps to unit_type for subunits)
// --------------------------------------------------------------------------

export type SubunitCategory = 'office' | 'storage' | 'shop' | 'custom'

export const SUBUNIT_CATEGORY_LABELS: Record<SubunitCategory, string> = {
  office: 'Office',
  storage: 'Storage',
  shop: 'Shop',
  custom: 'Custom',
}

export const SUBUNIT_CATEGORY_COLORS: Record<SubunitCategory, string> = {
  office: '#3B82F6',   // blue
  storage: '#F59E0B',  // amber
  shop: '#10B981',     // emerald
  custom: '#8B5CF6',   // violet
}

// --------------------------------------------------------------------------
// Database row type alias (subunits are rows in the units table)
// --------------------------------------------------------------------------

export type SubunitRow = Database['public']['Tables']['units']['Row']

// --------------------------------------------------------------------------
// Frontend domain types
// --------------------------------------------------------------------------

export interface Subunit {
  id: string
  organizationId: string
  locationId: string
  areaId: string
  parentUnitId: string

  // Identity
  name: string
  unitCode: string
  unitType: string // maps to SubunitCategory
  category: SubunitCategory

  // Dimensions (unit-local, feet)
  width: number
  depth: number
  height: number
  sqft: number

  // Position (relative to parent unit origin, feet)
  offsetX: number
  offsetY: number

  // Elevation
  elevationFt: number
  clearanceBufferFt: number

  // Space deduction
  deductFromParent: boolean

  // Status
  status: string

  // Metadata
  notes: string | null
}

export interface SubunitRenderData {
  subunits: Subunit[]
  /** Parent unit offset from building origin in feet [x, y] */
  parentUnitOffset: [number, number]
}

// --------------------------------------------------------------------------
// Input types for mutations
// --------------------------------------------------------------------------

export interface CreateSubunitInput {
  parentUnitId: string
  areaId: string
  locationId: string
  name: string
  unitCode: string
  category: SubunitCategory
  width: number
  depth: number
  height: number
  offsetX: number
  offsetY: number
  elevationFt?: number
  clearanceBufferFt?: number
  deductFromParent?: boolean
  notes?: string | null
}

export interface UpdateSubunitInput {
  name?: string
  unitCode?: string
  category?: SubunitCategory
  width?: number
  depth?: number
  height?: number
  offsetX?: number
  offsetY?: number
  elevationFt?: number
  clearanceBufferFt?: number
  deductFromParent?: boolean
  notes?: string | null
  status?: string
}

// --------------------------------------------------------------------------
// Mappers
// --------------------------------------------------------------------------

/** Map unit_type to SubunitCategory */
function toCategory(unitType: string): SubunitCategory {
  if (unitType === 'office' || unitType === 'storage' || unitType === 'shop') {
    return unitType as SubunitCategory
  }
  return 'custom'
}

/** Map DB row to frontend Subunit */
export function mapUnitRowToSubunit(row: SubunitRow): Subunit {
  const width = Number(row.interior_width ?? 0)
  const depth = Number(row.depth ?? 0)
  return {
    id: row.id,
    organizationId: row.organization_id,
    locationId: row.location_id,
    areaId: row.area_id,
    parentUnitId: row.parent_unit_id!,
    name: row.name ?? row.unit_code,
    unitCode: row.unit_code,
    unitType: row.type ?? 'custom',
    category: toCategory(row.type ?? 'custom'),
    width,
    depth,
    height: Number(row.interior_height ?? 10),
    sqft: width * depth,
    offsetX: Number(row.offset_x ?? 0),
    offsetY: Number(row.offset_y ?? 0),
    elevationFt: Number(row.elevation_ft ?? 0),
    clearanceBufferFt: Number(row.clearance_buffer_ft ?? 1.5),
    deductFromParent: row.deduct_from_parent ?? true,
    status: row.status ?? 'available',
    notes: null,
  }
}

// --------------------------------------------------------------------------
// Helpers
// --------------------------------------------------------------------------

/** Check if a unit row is a subunit (has parent_unit_id) */
export function isSubunit(row: SubunitRow): boolean {
  return row.parent_unit_id != null
}

/** Calculate effective sqft deducted from parent */
export function getEffectiveSqft(subunit: Subunit): number {
  return subunit.deductFromParent ? subunit.sqft : 0
}

/** Check if subunit is a mezzanine (elevation > 0) */
export function isMezzanine(subunit: Subunit): boolean {
  return subunit.elevationFt > 0
}

/** Get effective clearance height above the subunit */
export function getEffectiveClearance(subunit: Subunit, parentHeight: number): number {
  return parentHeight - subunit.elevationFt - subunit.height - subunit.clearanceBufferFt
}
